import { useEffect, useMemo, useState } from "react";

import { User, Users } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { StepShell } from "./StepShell";

const GENDERS = ["MALE", "FEMALE", "OTHER"];

const MARITAL_STATUSES = ["SINGLE", "MARRIED", "DIVORCED", "WIDOWED", "OTHER"];

const HOUSE_OWNERSHIP = [
  "OWNED",
  "RENTED",
  "PARENTAL",
  "COMPANY_PROVIDED",
  "OTHER",
];

/*
 * These are the fields that belong to Personal Details.
 *
 * Employment-specific fields such as:
 * - employmentType
 * - totalWorkExperienceMonths
 * - currentJobPeriodMonths
 * - officeName
 * - officeAddress
 * - officePincode
 * - employerName
 * - employerAddress
 * - employerDesignation
 *
 * are intentionally handled by EmploymentStep.
 */
const PRIMARY_FIELDS = [
  ["fullName", "Full Name", "text"],
  ["dateOfBirth", "Date of Birth", "date"],
  ["gender", "Gender", "select", GENDERS],
  ["maritalStatus", "Marital Status", "select", MARITAL_STATUSES],
  ["nationality", "Nationality", "text"],
  ["motherName", "Mother's Name", "text"],
  ["fatherName", "Father's Name", "text"],
  ["spouseName", "Spouse Name", "text"],
  ["qualification", "Qualification", "text"],
  ["dependents", "Dependents", "number", undefined, 0],
  ["monthlyIncome", "Monthly Income", "number", undefined, 0],
  ["phone", "Phone", "text"],
  ["email", "Email", "email"],
  ["currentAddress", "Current Address", "textarea"],
  ["currentPincode", "Current Pincode", "text"],
  ["houseOwnership", "House Ownership", "select", HOUSE_OWNERSHIP],
  ["permanentAddress", "Permanent Address", "textarea"],
  ["permanentPincode", "Permanent Pincode", "text"],
];

/**
 * Convert enum-style values into human-readable labels.
 */
function formatOptionLabel(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Determine whether a value contains usable data.
 */
function isFilled(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  return true;
}

/**
 * HTML <input type="date"> only accepts YYYY-MM-DD.
 *
 * MySQL/API responses can sometimes contain:
 * - YYYY-MM-DD
 * - YYYY-MM-DDTHH:mm:ss
 * - YYYY-MM-DDTHH:mm:ss.sssZ
 *
 * Always normalize the value before giving it to the input.
 */
function normalizeDateForInput(value) {
  if (!value) {
    return "";
  }

  const stringValue = String(value);

  /*
   * Already in the format required by input[type="date"].
   */
  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return stringValue;
  }

  /*
   * ISO datetime returned by the backend.
   */
  if (stringValue.includes("T")) {
    const datePart = stringValue.slice(0, 10);

    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }

  /*
   * Fallback for other valid date representations.
   */
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
}

/**
 * Convert backend applicant data into the exact shape
 * consumed by this component.
 *
 * Important:
 * - Never overwrite an existing value with undefined.
 * - Keep missing values as empty strings.
 * - Normalize date values for HTML date inputs.
 */
function normalizeApplicant(applicant = {}) {
  return {
    fullName: applicant.fullName ?? applicant.applicant_name ?? "",

    dateOfBirth: normalizeDateForInput(applicant.dateOfBirth),

    gender: applicant.gender ?? "",

    maritalStatus: applicant.maritalStatus ?? "",

    nationality: applicant.nationality ?? "",

    motherName: applicant.motherName ?? "",

    fatherName: applicant.fatherName ?? "",

    spouseName: applicant.spouseName ?? "",

    qualification: applicant.qualification ?? "",

    dependents: applicant.dependents ?? "",

    monthlyIncome: applicant.monthlyIncome ?? "",

    phone: applicant.phone ?? "",

    email: applicant.email ?? "",

    currentAddress: applicant.currentAddress ?? "",

    currentPincode: applicant.currentPincode ?? "",

    houseOwnership: applicant.houseOwnership ?? "",

    permanentAddress: applicant.permanentAddress ?? "",

    permanentPincode: applicant.permanentPincode ?? "",
  };
}

/**
 * Only send fields that actually contain data.
 *
 * This is important because the backend supports progressive
 * applicant updates. We do not want a later save to replace
 * previously stored values with empty/undefined values.
 */
function compactApplicant(applicant = {}) {
  const result = {};

  for (const [field] of PRIMARY_FIELDS) {
    const value = applicant?.[field];

    if (value !== undefined && value !== null && value !== "") {
      result[field] = value;
    }
  }

  return result;
}

function isApplicantFieldRequired(field, applicant) {
  if (field === "spouseName") {
    return applicant?.maritalStatus === "MARRIED";
  }

  return true;
}

function getRequiredApplicantFields(applicant) {
  return PRIMARY_FIELDS.filter(([field]) =>
    isApplicantFieldRequired(field, applicant),
  );
}

function isApplicantComplete(applicant) {
  if (!applicant) {
    return false;
  }

  const requiredFields = getRequiredApplicantFields(applicant);

  return requiredFields.every(([field]) => isFilled(applicant[field]));
}

export default function PersonalStep({ state, patch, setStep }) {
  /*
   * The portal API returns Personal Details as:
   *
   * state.personal_details
   *
   * Keep primaryApplicant as a backwards-compatible fallback.
   */
  const primaryApplicant = useMemo(
    () =>
      normalizeApplicant(
        state?.personal_details || state?.primaryApplicant || {},
      ),
    [state],
  );

  const coApplicant = useMemo(() => {
    if (!state?.co_applicant && !state?.coApplicant) {
      return null;
    }

    return normalizeApplicant(state?.co_applicant || state?.coApplicant || {});
  }, [state]);

  const [primary, setPrimary] = useState(primaryApplicant);

  const [co, setCo] = useState(coApplicant);

  const [showCoApplicant, setShowCoApplicant] = useState(Boolean(coApplicant));

  const [removeCoApplicant, setRemoveCoApplicant] = useState(false);

  const [saving, setSaving] = useState(false);

  /*
   * Synchronize local state whenever the parent portal
   * state is refreshed from the backend.
   *
   * This is critical when the user:
   *
   * 1. Saves Personal Details.
   * 2. Moves to another step.
   * 3. Comes back to Personal Details.
   *
   * The component must use the latest backend state.
   */
  useEffect(() => {
    setPrimary(primaryApplicant);
  }, [primaryApplicant]);

  useEffect(() => {
    setCo(coApplicant);
    setShowCoApplicant(Boolean(coApplicant));
  }, [coApplicant]);

  const primaryProgress = useMemo(() => {
    const requiredFields = getRequiredApplicantFields(primary);

    if (!requiredFields.length) {
      return 0;
    }

    const completed = requiredFields.filter(([field]) =>
      isFilled(primary[field]),
    ).length;

    return Math.round((completed / requiredFields.length) * 100);
  }, [primary]);

  const coProgress = useMemo(() => {
    if (!co) {
      return 0;
    }

    const requiredFields = getRequiredApplicantFields(co);

    if (!requiredFields.length) {
      return 0;
    }

    const completed = requiredFields.filter(([field]) =>
      isFilled(co[field]),
    ).length;

    return Math.round((completed / requiredFields.length) * 100);
  }, [co]);

  const updatePrimary = (field, value) => {
    setPrimary((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateCo = (field, value) => {
    setCo((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const addCoApplicant = () => {
    setCo(
      normalizeApplicant({
        nationality: "Indian",
      }),
    );

    setShowCoApplicant(true);
    setRemoveCoApplicant(false);
  };

  const removeCoApplicantHandler = () => {
    setCo(null);
    setShowCoApplicant(false);
    setRemoveCoApplicant(true);
  };

  /**
   * Save Personal Details.
   *
   * This saves only the Personal Details section.
   *
   * It does NOT enforce step ordering.
   *
   * The client is allowed to visit any portal step at any time.
   */
  const save = async () => {
    if (saving) {
      return;
    }

    if (!isApplicantComplete(primary)) {
      return;
    }

    if (showCoApplicant && !co) {
      return;
    }

    if (showCoApplicant && !isApplicantComplete(co)) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        primaryApplicant: compactApplicant(primary),

        removeCoApplicant: removeCoApplicant === true,
      };

      if (co) {
        payload.coApplicant = compactApplicant(co);
      }

      /*
       * patch() is responsible for:
       * - sending the PATCH request
       * - updating parent portal state
       * - handling API errors
       *
       * The returned backend state should therefore contain
       * the latest persisted Personal Details.
       */
      await patch("personal_details", payload);

      /*
       * Continue to the next step after successful save.
       *
       * There is deliberately NO validation of:
       * - current step
       * - previous steps
       * - future steps
       *
       * Step navigation is intentionally non-linear.
       */
      await setStep(2);
    } catch (error) {
      /*
       * patch() already handles/display errors.
       */
    } finally {
      setSaving(false);
    }
  };

  /*
   * Only prevent Save & Continue when the minimum required
   * Personal Details fields are missing.
   *
   * This does NOT prevent the client from clicking another
   * step in the portal.
   */
  const saveAndContinueDisabled =
    saving ||
    !isApplicantComplete(primary) ||
    (showCoApplicant && !co) ||
    (showCoApplicant && !isApplicantComplete(co));

  return (
    <StepShell
      testId="portal-personal"
      title="Personal Details"
      subtitle="Provide your personal, contact, address, family and other required details. Complete the details for the co-applicant as well."
      onBack={() => setStep(0)}
      onNext={save}
      nextLabel={saving ? "Saving..." : "Save & Continue"}
      disableNext={saveAndContinueDisabled}
    >
      <div className="space-y-6">
        <ApplicantSection
          title="Primary Applicant"
          icon={<User className="h-5 w-5" />}
          applicant={primary}
          progress={primaryProgress}
          onChange={updatePrimary}
        />

        <div className="rounded-xl border bg-card">
          <div className="flex items-center justify-between gap-4 border-b p-5">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5" />

              <div>
                <h2 className="font-semibold">Co-Applicant</h2>
              </div>
            </div>

            {!showCoApplicant ? (
              <Button
                type="button"
                variant="outline"
                onClick={addCoApplicant}
                disabled={saving}
              >
                Add Co-Applicant
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                onClick={removeCoApplicantHandler}
                disabled={saving}
              >
                Remove
              </Button>
            )}
          </div>

          {showCoApplicant && co && (
            <div className="p-5">
              <ApplicantSection
                title=""
                applicant={co}
                progress={coProgress}
                onChange={updateCo}
                compact
              />
            </div>
          )}
        </div>
      </div>
    </StepShell>
  );
}

function ApplicantSection({
  title,
  icon,
  applicant,
  progress,
  onChange,
  compact = false,
}) {
  return (
    <div className="rounded-xl border bg-card">
      {!compact && (
        <div className="flex items-center justify-between border-b p-5">
          <div className="flex items-center gap-3">
            {icon}

            <div>
              <h2 className="font-semibold">{title}</h2>

              <p className="text-sm text-muted-foreground">
                Complete your personal and contact information.
              </p>
            </div>
          </div>

          <div className="text-sm font-medium">{progress}%</div>
        </div>
      )}

      <div className="grid gap-5 p-5 md:grid-cols-2">
        {PRIMARY_FIELDS.map(([field, label, type, options, min]) => (
          <Field
            key={field}
            label={label}
            type={type}
            value={applicant?.[field] ?? ""}
            options={options || []}
            min={min}
            required={isApplicantFieldRequired(field, applicant)}
            onChange={(value) => onChange(field, value)}
          />
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  type = "text",
  options = [],
  min,
  onChange,
  required = false,
}) {
  if (type === "select") {
    return (
      <div>
        <Label>
          {label} {required && <span className="ml-1 text-red-500">*</span>}
        </Label>

        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="mt-2">
            <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
          </SelectTrigger>

          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {formatOptionLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className="md:col-span-2">
        <Label>
          {label} {required && <span className="ml-1 text-red-500">*</span>}
        </Label>

        <Textarea
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
          className="mt-2"
          rows={3}
        />
      </div>
    );
  }

  return (
    <div>
      <Label>{label} {required && (
    <span className="ml-1 text-red-500">*</span>
  )}</Label>

      <Input
        type={type}
        value={value ?? ""}
        min={min}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2"
      />
    </div>
  );
}
